import {
  forwardRef,
  type ReactElement,
  type Ref,
  type SyntheticEvent,
  useMemo,
  useRef,
} from 'react';
import { PropsValue, SingleValue } from 'react-select';

import { VisuallyHidden } from '../../../core/components/visually-hidden/VisuallyHidden.js';
import { useId } from '../../../core/hooks/useId.js';
import { useMergeRefs } from '../../../core/hooks/useMergeRefs.js';
import type { FormControlState } from '../../../core/types/form-control-state.js';
import { useFormFieldValidation } from '../../forms-core/hooks/useFormFieldValidation.js';
import type { BaseOption } from '../types.js';

//#region Typings
/** Accepted properties for the HiddenSelect component */
interface HiddenSelectProps<OptionValue> {
  ref: Ref<HTMLSelectElement>;
  name?: string;
  form?: string;
  selectedValues?: PropsValue<OptionValue>;
  required?: boolean;
  disabled?: boolean;
  ariaDisabled?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-deprecated -- APPDEV-18321
  controlState?: FormControlState;
}
//#endregion

/**
 * HiddenSelect component
 * @internal
 */
export const HiddenSelect: <OptionValue>(
  props: HiddenSelectProps<OptionValue>,
) => /* @__PURE__ */ ReactElement | null = forwardRef(
  /* eslint-disable @typescript-eslint/no-unnecessary-type-constraint */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  <OptionValue extends any>(
    props: HiddenSelectProps<OptionValue>,
    forwardedRef: HiddenSelectProps<OptionValue>['ref'],
  ) => {
    const {
      name,
      form,
      selectedValues,
      required,
      disabled,
      ariaDisabled,
      controlState,
    } = props;
    const multiple = Array.isArray(selectedValues);

    const id = useId();
    const ref = useRef<HTMLSelectElement | null>(null);

    const selectedValuesArray = useMemo(() => {
      // Use textValue if it's defined. If not, use label (only if it's a string). Otherwise, use the value property.
      const extractValue = (
        val: SingleValue<BaseOption<OptionValue>>,
      ): string => {
        return val?.textValue ?? '';
      };

      if (multiple) {
        return selectedValues?.length > 0
          ? selectedValues.map(extractValue)
          : [];
      }

      if (selectedValues) {
        return [
          extractValue(
            selectedValues as unknown as SingleValue<BaseOption<OptionValue>>,
          ),
        ];
      }

      return [];
    }, [multiple, selectedValues]);

    const { ariaProps, onBlur, onChange, onInvalid } = useFormFieldValidation(
      ref,
      // selectedValuesArray already contains only plain strings, so it is safe
      // to join for a dep key — no risk of BigInt or circular-reference errors.
      { selectedValues: selectedValuesArray.join(',') },
      {},
      id,
    );

    const handleInvalid = controlState
      ? (event: SyntheticEvent<HTMLSelectElement>) => {
          // Suppress the native browser validation popup.
          event.preventDefault();
        }
      : onInvalid;

    const value = useMemo(() => {
      if (multiple) {
        return selectedValuesArray;
      }
      // use string value or empty string as array values are not allowed for single select
      return selectedValues
        ? `${
            (selectedValues as unknown as SingleValue<BaseOption<OptionValue>>)
              ?.value
          }`
        : '';
    }, [multiple, selectedValues, selectedValuesArray]);

    return (
      <VisuallyHidden>
        <select
          ref={useMergeRefs([forwardedRef, ref])}
          name={name}
          form={form}
          multiple={multiple}
          tabIndex={-1}
          required={required}
          disabled={disabled}
          aria-disabled={ariaDisabled}
          aria-hidden
          value={value && value.length !== 0 ? value : undefined}
          id={id}
          onChange={() => undefined}
          onInvalid={handleInvalid}
          {...(controlState ? {} : { ...ariaProps, onBlur, onChange })}
        >
          {selectedValuesArray.map((val, key) => {
            return <option value={`${val}`} key={`${key}-${val}`} />;
          })}
        </select>
      </VisuallyHidden>
    );
  },
);

(HiddenSelect as typeof HiddenSelect & { displayName: string }).displayName =
  'HiddenSelect';
